import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import CT from '@marketplace-ai/tags';
import { useEffect, useRef, useState } from 'react';

import { useAlertView, useConfirmationView } from '~/app';
import { apiAxiosV1 } from '~/utils/service';
import { captureSession } from '~/utils/sessionBoundary';
import {
  parseFilenameFromContentDisposition,
  triggerBrowserDownload,
} from './DownloadMyDataLink.helpers';

const TAG = CT.layout.navDrawer.downloadMyDataLink;

type Status = 'idle' | 'downloading' | 'error';

type Props = {
  providerId: number;
  compact?: boolean;
};

export const DownloadMyDataLink = ({ providerId, compact = false }: Props) => {
  const [status, setStatus] = useState<Status>('idle');
  const alertView = useAlertView();
  const confirmationRef = useConfirmationView();
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // [MCV-1572] The in-flight guard must be a ref, not the `status` state.
  // performDownload is now handed to the ConfirmationDialog as `onConfirm`, so
  // the copy the dialog holds is captured from the render that opened it and
  // keeps that render's `status` forever. A second invocation of that SAME
  // captured closure (double-clicking the dialog's Confirm button, which fires
  // before React re-renders) would therefore still read `status === 'idle'` and
  // start a second export. A ref is read live and closes that hole.
  const inFlightRef = useRef(false);

  useEffect(() => {
    return () => {
      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current);
      }
    };
  }, []);

  // [MCV-1572] The actual export request — unchanged from the old handleClick,
  // just relocated so it only runs once the shared ConfirmationDialog's
  // onConfirm invokes it, instead of firing straight off the click.
  const performDownload = async () => {
    if (inFlightRef.current) return;
    // Cancel any pending error-state reset from a prior failure so it can't
    // fire mid-retry and flip status back to 'idle' while a new download is in flight.
    if (resetTimerRef.current) {
      clearTimeout(resetTimerRef.current);
      resetTimerRef.current = null;
    }
    inFlightRef.current = true;
    setStatus('downloading');
    // The export is one account's vault. If the session changes while it is being
    // prepared, the finished file must not be saved on the next account's behalf.
    const session = captureSession();
    try {
      const response = await apiAxiosV1.get<Blob>(`/vault-data/${providerId}/export`, {
        responseType: 'blob',
      });
      if (!session.isCurrent()) {
        setStatus('idle');
        return;
      }
      const headers = new Headers();
      Object.entries(response.headers ?? {}).forEach(([k, v]) => {
        if (typeof v === 'string') headers.set(k, v);
      });
      const filename =
        parseFilenameFromContentDisposition(headers) ?? `vault-export-${providerId}.zip`;
      triggerBrowserDownload(response.data as Blob, filename);
      setStatus('idle');
    } catch (err) {
      if (!session.isCurrent()) {
        setStatus('idle');
        return;
      }
      console.error('Vault export failed', err);
      alertView.send({
        type: 'ON_SHOW_ALERT',
        payload: {
          text: 'Could not download your data',
          subText: 'Please try again.',
          type: 'error',
          timeout: 5000,
        },
      });
      setStatus('error');
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
      resetTimerRef.current = setTimeout(() => {
        setStatus('idle');
        resetTimerRef.current = null;
      }, 5000);
    } finally {
      inFlightRef.current = false;
    }
  };

  // [MCV-1572] QA asked for a confirmation step before the vault export fires.
  // Both render modes below (compact icon-only + full label) share this one
  // handler, so the confirm gate covers both without duplicating it. Cancel
  // routes through the dialog's own Cancel button and never reaches here, so
  // it issues no request.
  const handleClick = () => {
    if (status === 'downloading') return;
    confirmationRef?.send({
      type: 'ON_SHOW_CONFIRMATION',
      payload: {
        title: 'Download my data',
        message: 'This downloads a zip file of your vault data to this device.',
        // [MCV-1572] Exporting your own data is not destructive, so it does not
        // get the red button and warning triangle. The dialog defaults to the
        // destructive tone (and to a 'Delete' confirm label), which is right for
        // the delete-account style callers but would misrepresent this one.
        confirmLabel: 'Download',
        tone: 'neutral',
        onConfirm: performDownload,
      },
    });
  };

  const disabled = status === 'downloading';

  if (compact) {
    return (
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled}
        aria-label="Download my data"
        title="Download my data"
        data-cy={TAG}
        className="flex items-center justify-center p-2 rounded-md cursor-pointer text-grey-200 hover:bg-white/50 transition-colors duration-150 disabled:cursor-wait disabled:opacity-60"
      >
        <ArrowDownTrayIcon className="size-5 text-grey-400" />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      data-cy={TAG}
      className="flex items-center gap-2.5 px-3 py-2 rounded-md cursor-pointer text-grey-200 hover:bg-white/50 transition-colors duration-150 disabled:cursor-wait disabled:opacity-60 w-full text-left"
    >
      <ArrowDownTrayIcon className="size-4 text-grey-400" />
      <span className="text-sm">{disabled ? 'Preparing…' : 'Download My Data'}</span>
    </button>
  );
};
